<template>
  <div class="create-comment-preview">
    <div
      class="single-obituary-comment create-comment-preview__card"
      :class="{ 'use-fallback-svg': !firstFile }"
    >
      <div class="single-obituary-comment__container">
        <div>
          <div
            class="single-obituary-comment__image-place"
            :class="{ inactive: !firstFile, 'no-image': !firstFile?.preview }"
          >
            <button
              class="create-comment-preview__open"
              type="button"
              v-if="firstFile"
              :aria-label="texts.common.openImage"
              :disabled="!viewable.length"
              @click="lightboxOpen = true"
            >
              <img
                alt=""
                class="create-comment-preview__image"
                v-if="firstFile.preview"
                :src="firstFile.preview"
              >
              <svg aria-hidden="true" class="icon icon--3x" v-else>
                <use href="#photo-film" />
              </svg>
              <span
                class="single-obituary-comment__image-icon multiple-media"
                v-if="files.length > 1"
              >
                <svg aria-hidden="true" class="icon">
                  <use href="#images" />
                </svg>
              </span>
              <span
                class="single-obituary-comment__image-icon open-image"
                v-if="viewable.length"
              >
                <svg aria-hidden="true" class="icon">
                  <use href="#arrows-maximize" />
                </svg>
              </span>
            </button>

            <template v-else>
              <svg
                aria-hidden="true"
                class="single-obituary-comment__fallback-svg"
              >
                <use href="#memorial-comment-candle" />
              </svg>
              <button
                class="create-comment-preview__add"
                type="button"
                :disabled="publishing"
                @click="chooseMedia"
              >
                <svg aria-hidden="true" class="icon">
                  <use href="#plus" />
                </svg>
                <span v-text="texts.createComment.addMediaHere" />
              </button>
            </template>
          </div>
        </div>

        <div class="single-obituary-comment__content">
          <div class="single-obituary-comment__top-bar">
            <div class="name-wrapper">
              <p class="single-obituary-comment__name" v-text="name" />
              <span
                class="single-obituary-comment__date"
                v-text="texts.createComment.justNow"
              />
            </div>

            <div class="single-obituary-comment__info" v-if="donation">
              <div class="single-obituary-comment__donation">
                <span>
                  <svg aria-hidden="true" class="icon icon--large icon--hflip">
                    <use href="#hand-holding-heart" />
                  </svg>
                  <span
                    v-text="
                      donation.recurringDonation
                        ? texts.singleComment.hasMadeRecurringDonation
                        : texts.singleComment.hasDonated
                    "
                  />
                </span>
              </div>
            </div>
          </div>

          <p
            class="single-obituary-comment__text create-comment-preview__text"
            v-text="comment"
          />
        </div>
      </div>
    </div>

    <p class="create-comment-preview__tip" v-if="!firstFile">
      <svg aria-hidden="true" class="icon">
        <use href="#image" />
      </svg>
      <span v-text="texts.createComment.previewTip" />
    </p>

    <div
      class="obituary-comments__submit-container create-comment-query__actions"
    >
      <template v-if="firstFile">
        <button
          class="btn cta"
          type="button"
          :class="publishing && 'disabled'"
          @click="publishGreeting"
        >
          <span v-text="texts.createComment.publishCta" />
        </button>
        <button
          class="btn outline"
          type="button"
          :disabled="publishing"
          @click="edit"
        >
          <span v-text="texts.createComment.editCta" />
        </button>
      </template>

      <template v-else>
        <button
          class="btn cta"
          type="button"
          :disabled="publishing"
          @click="chooseMedia"
        >
          <span v-text="texts.createComment.chooseMediaCta" />
        </button>
        <button
          class="btn outline"
          type="button"
          :class="publishing && 'disabled'"
          @click="publishGreeting"
        >
          <span v-text="texts.createComment.publishWithoutMediaCta" />
        </button>
      </template>
    </div>

    <create-comment-lightbox
      v-if="lightboxOpen"
      :files="viewable"
      @close="lightboxOpen = false"
    />
  </div>
</template>

<script>
  import Helpers from "Modules/Helpers";
  import { mapActions, mapState } from "vuex";
  import { useCreateComment } from "../../composables/useCreateComment";
  import { FORM, MEDIA_INITIAL, PREVIEW } from "../../createCommentStepsHelper";
  import { openMediaPicker } from "../../media-picker";
  import CreateCommentLightbox from "./CreateCommentLightbox.vue";

  export default {
    components: { CreateCommentLightbox },

    computed: {
      ...mapState("donations", ["donation"]),

      firstFile() {
        return this.files?.[0] ?? null;
      },

      // HEIC photos have no thumbnail and can't be shown in most browsers.
      viewable() {
        return (this.files ?? []).filter((f) => f.full || f.preview);
      },
    },

    data() {
      return {
        lightboxOpen: false,
        texts: lavendlaObituary,
      };
    },

    methods: {
      ...mapActions("createComment", [
        "addPrevContext",
        "changeModalContext",
        "updateSteps",
      ]),

      backToForm(context, logAction) {
        Helpers.obituaryGreetingsLog(logAction, this.orderInfo.obituaryId);
        this.addPrevContext(PREVIEW);
        this.changeModalContext(context);
      },

      chooseMedia() {
        openMediaPicker();
        this.backToForm(MEDIA_INITIAL, "PreviewAddMedia");
      },

      edit() {
        this.backToForm(FORM, "PreviewEdit");
      },

      publishGreeting() {
        if (!this.firstFile) {
          Helpers.obituaryGreetingsLog(
            "PreviewPublishedWithoutMedia",
            this.orderInfo.obituaryId
          );
        }

        this.publish(this.orderInfo);
      },
    },

    mounted() {
      this.updateSteps("preview");
      Helpers.obituaryGreetingsLog("Preview", this.orderInfo.obituaryId);
    },
    name: "CreateCommentPreview",

    props: {
      orderInfo: {
        required: true,
        type: Object,
      },
    },

    setup() {
      const { comment, files, name, publish, publishing } = useCreateComment();

      return { comment, files, name, publish, publishing };
    },
  };
</script>
